import type { PermissionAction, PermissionSubject, WorkspaceDetail } from '@seventide/types';
import { useReadOnlyFlag } from '../lib/readOnly';
import { useOptionalWorkspace } from './useWorkspace';

export type AccessBlock = 'read_only' | 'archive' | null;

/** Why edits/exports are blocked in this workspace (FR-13.2 read-only, FR-10.1 archive), or null. */
export function accessBlock(ws: WorkspaceDetail, readOnlyFlag = false): AccessBlock {
  if (ws.subscription.readOnly || readOnlyFlag) return 'read_only';
  if (ws.archive && ws.membership.role !== 'owner') return 'archive';
  return null;
}

/**
 * Pure permission check used by `useCan`. Uses the server-computed `permissions` map (spec §3.2,
 * `permissionMap()` in @seventide/types) and layers read-only/archive on top for edit/export.
 */
export function canInWorkspace(
  ws: WorkspaceDetail,
  subject: PermissionSubject,
  action: PermissionAction,
  readOnlyFlag = false,
): boolean {
  const allowed = ws.permissions?.[subject]?.[action] === true;
  if (!allowed) return false;
  if (action === 'view') return true;
  // Billing must stay reachable so a read-only workspace can be renewed.
  if (subject === 'billing') return true;
  return accessBlock(ws, readOnlyFlag) === null;
}

/** `const canEdit = useCan('gifts', 'edit')` — false outside a workspace. */
export function useCan(subject: PermissionSubject, action: PermissionAction): boolean {
  const ctx = useOptionalWorkspace();
  const { readOnly } = useReadOnlyFlag();
  if (!ctx) return false;
  return canInWorkspace(ctx.workspace, subject, action, readOnly);
}

/** Current block reason for banners and disabled-button hints. */
export function useAccessBlock(): AccessBlock {
  const ctx = useOptionalWorkspace();
  const { readOnly } = useReadOnlyFlag();
  if (!ctx) return readOnly ? 'read_only' : null;
  return accessBlock(ctx.workspace, readOnly);
}
